import { createFileRoute, Link } from "@tanstack/react-router";
import { Sparkles, ArrowLeft } from "lucide-react";
import { LandingFooter } from "@/components/LandingFooter";

import journalAsset from "@/assets/home/journal-illustration.png.asset.json";
import communityAsset from "@/assets/home/community-illustration.png.asset.json";
import breathingAsset from "@/assets/home/breathing-illustration.png.asset.json";
import groundingAsset from "@/assets/home/grounding-illustration.png.asset.json";
import somaticAsset from "@/assets/home/somatic-illustration.png.asset.json";
import therapistAsset from "@/assets/home/therapy-approaches.png.asset.json";
import soundsAsset from "@/assets/home/sounds-illustration.png.asset.json";
import eftAsset from "@/assets/home/eft-illustration.png.asset.json";

const TOUR_OG =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/bda540b3-55c7-4373-af6d-b5588ecb7c64";

export const Route = createFileRoute("/guest/tour")({
  head: () => ({
    meta: [
      { title: "Platform Tour — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A 2–3 minute guided tour of Recovery Trauma™ — dashboards, journals, community, therapists, healing library and more.",
      },
      { property: "og:title", content: "Platform Tour — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "See inside Recovery Trauma™ — a calm, guided walk-through of every core area of the platform.",
      },
      { property: "og:url", content: "https://recoverytrauma.com/guest/tour" },
      { property: "og:image", content: TOUR_OG },
      { name: "twitter:image", content: TOUR_OG },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/guest/tour" }],
  }),
  component: TourPage,
});

type Stop = {
  index: string;
  emoji: string;
  title: string;
  body: string;
  image?: string;
};

const STOPS: Stop[] = [
  {
    index: "01",
    emoji: "🏡",
    title: "Your Dashboard",
    body: "A calm home base that adapts to how you're feeling — quick access to journals, grounding tools and today's affirmation.",
  },
  {
    index: "02",
    emoji: "💛",
    title: "Recovery Journal",
    body: "Private, guided prompts for the recovery journey — trauma-informed and paced for regulation, not overwhelm.",
    image: journalAsset.url,
  },
  {
    index: "03",
    emoji: "🌙",
    title: "Dream Journal",
    body: "Capture what surfaces at night and gently explore recurring themes over time.",
  },
  {
    index: "04",
    emoji: "🤝",
    title: "Community",
    body: "A moderated, private space with people who understand — no algorithms, no pressure, just presence.",
    image: communityAsset.url,
  },
  {
    index: "05",
    emoji: "🩺",
    title: "Therapist Directory",
    body: "Browse trauma-informed therapists worldwide. Filter by approach, specialism and availability.",
    image: therapistAsset.url,
  },
  {
    index: "06",
    emoji: "🎨",
    title: "Healing Library",
    body: "Worksheets, guides and creative reflection tools — beautifully designed, downloadable and printable.",
    image: eftAsset.url,
  },
  {
    index: "07",
    emoji: "📈",
    title: "Progress Tracking",
    body: "Softly see your patterns and growth over time — never a score, always a mirror.",
  },
  {
    index: "08",
    emoji: "🧠",
    title: "Daily Check-ins",
    body: "A gentle nervous-system snapshot to notice what's shifting from day to day.",
  },
  {
    index: "09",
    emoji: "🫁",
    title: "Breathing & Grounding",
    body: "Guided, illustrated breathwork and grounding practices to return to the present moment.",
    image: breathingAsset.url,
  },
  {
    index: "10",
    emoji: "🌿",
    title: "Grounding Practices",
    body: "Sensory anchors — sight, sound, touch — to steady the body when things feel too big.",
    image: groundingAsset.url,
  },
  {
    index: "11",
    emoji: "🧘",
    title: "Somatic Healing",
    body: "Body-based practices for stored trauma, drawn from Levine, Porges and Van der Kolk's work.",
    image: somaticAsset.url,
  },
  {
    index: "12",
    emoji: "📅",
    title: "Events",
    body: "Live circles, workshops and expert sessions — a real calendar of gatherings, all included.",
    image: soundsAsset.url,
  },
  {
    index: "13",
    emoji: "✨",
    title: "Membership",
    body: "Lock in founding pricing for life. Full access to every tool, every event, every gentle corner of the platform.",
  },
];

function TourPage() {
  return (
    <div className="relative">
      <div
        aria-hidden
        className="bg-blob-warm pointer-events-none absolute inset-x-0 top-0 -z-10 h-[600px] opacity-70"
      />

      <div className="mx-auto max-w-4xl px-4 py-10 sm:py-14">
        <Link
          to="/guest"
          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground/60 hover:text-[var(--gold-deep)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Guest Access
        </Link>

        <header className="mt-6 text-center animate-fade-in">
          <span className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-gold-light/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
            <Sparkles className="h-3 w-3" /> 2–3 minute tour
          </span>
          <h1 className="mt-4 font-display text-3xl leading-[1.1] tracking-tight text-foreground sm:text-5xl">
            A guided look inside Recovery Trauma<span className="text-[var(--gold-deep)]">™</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-foreground/75 sm:text-base">
            Scroll gently through each core area of the platform. Take your time — there's
            no timer, and nothing here asks anything of you.
          </p>
        </header>

        <ol className="mt-14 space-y-10 sm:space-y-14">
          {STOPS.map((stop, i) => (
            <li
              key={stop.index}
              className="relative grid gap-6 sm:grid-cols-[auto_1fr] sm:gap-8"
            >
              <div className="flex sm:flex-col sm:items-center">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full border border-gold/40 bg-card text-2xl shadow-[var(--shadow-soft)]">
                  {stop.emoji}
                </div>
                {i < STOPS.length - 1 && (
                  <div
                    aria-hidden
                    className="ms-6 hidden w-px flex-1 bg-gradient-to-b from-gold/50 to-transparent sm:mx-auto sm:ms-0 sm:mt-3 sm:block"
                  />
                )}
              </div>

              <div className="rounded-3xl border border-gold/25 bg-card p-5 shadow-[var(--shadow-soft)] sm:p-6">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
                  Stop {stop.index}
                </p>
                <h2 className="mt-1.5 font-display text-xl text-foreground sm:text-2xl">
                  {stop.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground/75 sm:text-base">
                  {stop.body}
                </p>
                {stop.image && (
                  <div className="mt-4 overflow-hidden rounded-2xl border border-gold/25 bg-gold-light/20">
                    <img
                      src={stop.image}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="mx-auto block max-h-56 w-auto object-contain"
                    />
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>

        <div className="sticky bottom-4 mt-14 flex justify-center">
          <Link
            to="/membership"
            className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-[var(--gradient-gold)] px-7 py-3 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)] ring-1 ring-gold/50 transition-all duration-300 hover:-translate-y-0.5 sm:text-base"
          >
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full"
            />
            <Sparkles className="h-4 w-4" />
            Become a Member
          </Link>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}
